import { useMemo } from "react";
import { CheckCircle2 } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import type { NonCompliantBreakdown } from "../../types/logReview";
import {
  BAR_FILL,
  MARK_STROKE_WIDTH,
  formatCompact,
  markGlow,
} from "../../components/ChartsComponent";
import { severityColor } from "./logReviewChartUtils";

/**
 * Distribution panel for the Non-Compliant Findings tile: a semicircle gauge
 * split by severity (each arc labelled with its total + new-findings delta)
 * with a centre grand total, a per-severity Non-Compliant / Acknowledged
 * breakdown, and a footer summary bar. Counts render compact (`1.2K`) so
 * large totals never overflow an arc.
 */
export interface NonCompliantDistributionPanelProps {
  breakdown: NonCompliantBreakdown;
}

/** Fixed height so the semicircle geometry resolves — a % height collapses. */
const GAUGE_HEIGHT = 240;

/** Arcs thinner than this can't hold the 3-line label without spilling. */
const MIN_FULL_LABEL_SHARE = 0.15;

interface ArcDatum {
  name: string;
  value: number;
  color: string;
  delta: string;
  share: number;
}

interface ArcLabelProps {
  cx?: number;
  cy?: number;
  midAngle?: number;
  innerRadius?: number;
  outerRadius?: number;
  index?: number;
}

export const NonCompliantDistributionPanel = ({
  breakdown,
}: NonCompliantDistributionPanelProps) => {
  const { total, newCount, newPercent, severities } = breakdown;

  const arcs = useMemo<ArcDatum[]>(() => {
    const sum = severities.reduce((s, x) => s + x.nonCompliant + x.acknowledged, 0);
    return severities.map((s) => {
      const value = s.nonCompliant + s.acknowledged;
      return {
        name: s.label,
        value,
        color: severityColor(s.label),
        delta: `${formatCompact(s.newCount)} ▲ ${s.newPercent}%`,
        share: sum > 0 ? value / sum : 0,
      };
    });
  }, [severities]);

  // Labels sit inside their arc, so they are drawn white against the fill and
  // positioned from the slice's mid-angle rather than by recharts' default
  // outside placement.
  const renderArcLabel = ({
    cx = 0,
    cy = 0,
    midAngle = 0,
    innerRadius = 0,
    outerRadius = 0,
    index = 0,
  }: ArcLabelProps) => {
    const arc = arcs[index];
    if (!arc || arc.value <= 0) return <g key={`label-${index}`} />;

    const radius = innerRadius + (outerRadius - innerRadius) / 2;
    const rad = -midAngle * (Math.PI / 180);
    const x = cx + radius * Math.cos(rad);
    const y = cy + radius * Math.sin(rad);
    const compact = arc.share < MIN_FULL_LABEL_SHARE;

    // The arc is no longer filled, so the label sits on the card surface
    // rather than on a band of colour. White would vanish in light theme --
    // it wears the arc's own hue instead, the same rule the treemap follows.
    return (
      <g key={`label-${index}`}>
        {!compact ? (
          <text
            x={x}
            y={y - 16}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={arc.color}
            fontSize={12}
            fontWeight={600}
          >
            {arc.name}
          </text>
        ) : null}
        <text
          x={x}
          y={compact ? y : y + 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={arc.color}
          fontSize={20}
          fontWeight={700}
        >
          {formatCompact(arc.value)}
        </text>
        {!compact ? (
          <text
            x={x}
            y={y + 20}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={arc.color}
            fillOpacity={0.85}
            fontSize={10}
          >
            {arc.delta}
          </text>
        ) : null}
      </g>
    );
  };

  const hasArcs = arcs.some((a) => a.value > 0);

  return (
    <div className="flex h-full flex-col justify-between" data-testid="nc-distribution-panel">
      {/* Semicircle gauge + centre total */}
      <div className="relative" style={{ height: GAUGE_HEIGHT }}>
        {hasArcs ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={arcs}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="82%"
                startAngle={180}
                endAngle={0}
                innerRadius="88%"
                outerRadius="150%"
                paddingAngle={2}
                cornerRadius={4}
                isAnimationActive={false}
                labelLine={false}
                label={renderArcLabel}
              >
                {/* Outline-only arcs, matching the bars and the donut: the
                    severity hue lives on the edge and in the glow, not in a
                    solid band of colour. */}
                {arcs.map((a) => (
                  <Cell
                    key={a.name}
                    fill={BAR_FILL}
                    stroke={a.color}
                    strokeWidth={MARK_STROKE_WIDTH}
                    style={{ filter: markGlow(a.color) }}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        ) : null}
        <div
          className="pointer-events-none absolute flex flex-col items-center text-center"
          style={{ left: "50%", top: "82%", transform: "translate(-50%, -100%)" }}
        >
          <span className="text-xs text-muted-foreground">Total</span>
          <span className="text-2xl font-bold leading-none tabular-nums text-foreground">
            {formatCompact(total)}
          </span>
          <span className="text-xs text-muted-foreground">Findings</span>
        </div>
      </div>

      {/* Per-severity Non-Compliant / Acknowledged breakdown */}
      <div className="grid grid-cols-3 gap-3 border-t border-border pt-3">
        {severities.map((s) => {
          const color = severityColor(s.label);
          return (
            <div
              key={s.label}
              className="flex flex-col items-center gap-1 text-center"
              data-testid={`nc-distribution-card-${s.label}`}
            >
              <span className="text-sm font-bold" style={{ color }}>
                {s.label}
              </span>
              <div className="flex flex-col gap-0.5 text-xs tabular-nums text-muted-foreground">
                <span>Non-Compliant: {formatCompact(s.nonCompliant)}</span>
                <span>Acknowledged: {formatCompact(s.acknowledged)}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer summary bar */}
      <div className="mt-3 flex items-center justify-between rounded-lg bg-muted px-3 py-2 text-sm">
        <span className="flex items-center gap-2 font-medium text-muted-foreground">
          <CheckCircle2 className="size-4 text-emerald-500" strokeWidth={2.5} />
          {formatCompact(newCount)} New Findings
        </span>
        <span className="font-semibold tabular-nums text-muted-foreground">
          {newPercent}%*
        </span>
      </div>
    </div>
  );
};
